// Decode the DMP `delivery_status`. TWO schemes coexist and both are decoded here (see
// NEW_DELIVERY_STATUS for the newer one and why neither can be dropped):
//   - the NEW flat scheme 1101-1120, in force from 1 September 2026;
//   - the 4-digit composite below, carried by the ~396 000 addresses encoded before it.
//
// The composite is read digit-by-digit:
//   1st digit   = deployment type   (7=UG, 8=Façade, 9=Aerial, 2=Unit is blocked)
//   2nd+3rd     = network type      (01..07)
//   4th digit   = delivery status   (1..6)
// Example: 9055 → Aerial · Pole-Dropbox Quadrant P (SDU) · HP HCA.
// Unknown sub-codes degrade gracefully (the raw code is always shown).
//
// SOURCE OF TRUTH for delivery-status decoding, shared by the frontend (live decode/preview in the
// validation window + the Create-address form) and the API (address `status` + fiber
// `deployment_type`/`quadrant` derivations on create). The frontend lib `src/lib/deliveryStatus.ts`
// re-exports from here, so existing callers are unchanged.
export const DEPLOYMENT = {
    '7': 'UG',
    '8': 'Façade',
    '9': 'Aerial',
    '2': 'Unit is blocked',
};
export const NETWORK = {
    '01': 'Floor-Box, Quadrant A (MDU)',
    '02': 'Budi-Box, Quadrant C (MDU)',
    '03': 'Facade-Dropbox, Quadrant D (MDU)',
    '04': 'Facade-Dropbox, Quadrant D (SDU)',
    '05': 'Pole-Dropbox, Quadrant P (SDU)',
    '06': 'Multifu, Quadrant M (SDU)',
    '07': 'HP Plastic Multifu, Quadrant M+ (SDU)',
};
export const DELIVERY = {
    '1': 'not planned',
    '2': 'planned',
    '3': 'HP Wip',
    '4': 'HP WIP Ready for optical work',
    '5': 'HP HCA',
    '6': 'Homes Connected',
};
// `address_fiber.deployment_type` stand-in. The Symfony DeliveryStatusHelper stores the V3 NETWORK type
// (digits 2-3 — Floor Box / Budi Box / … via getV3DeploymentNetworkType) in this column, NOT the digit-1
// deployment family (UG/Façade/Aerial). We key on the network group to match it; local column is free text.
const FIBER_NETWORK_TYPE = {
    '00': 'NOT_YET_KNOWN',
    '01': 'FLOOR_BOX',
    '02': 'BUDI_BOX',
    '03': 'FACADE_DROPBOX',
    '04': 'FACADE_DROPBOX',
    '05': 'POLE_DROPBOX',
    '06': 'MULTIFU',
    '07': 'FACADE_PLASTIC_BOX',
    '08': 'OTHER_SIDE_OF_STREET',
};
// Quadrant letter derived from the 2nd+3rd digits (extracted from the NETWORK labels above).
const QUADRANT = {
    '01': 'A',
    '02': 'C',
    '03': 'D',
    '04': 'D',
    '05': 'P',
    '06': 'M',
    '07': 'M+',
};
// BLOCKED delivery codes (user annex 2026-08-03): two FLAT per-code reason tables that OVERRIDE the
// positional decode — 2001-2015 « Blocked » and 2101-2112 « Not-Homes Passed » are specific blocking
// reasons, NOT 4-digit composites (2012 is « Blocked by Unifiber decision », not Floor-Box/planned).
export const BLOCKED_DELIVERY_REASONS = {
    2001: 'Address has been identified as not existing',
    2002: 'Building is in renovation',
    2003: 'No trenching permit obtained',
    2004: 'Sper periode',
    2005: 'Synergy delayed',
    2006: 'GRD (Ores/Resa/REW/AIEG)',
    2007: 'Outside building obstruction (building blocked by another building, building empty, protected garden/façade, …)',
    2008: 'Inside building obstruction (inside cabling not allowed, plastic cabletray not allowed, no inside technical solution, protected building, …)',
    2009: 'Blocked by syndic or promotor (agreement not yet obtained, agreement rejected, no access granted into the building, …)',
    2010: 'Unit inside the polygon but not foreseen to be connected (not a residential, business or public building)',
    2011: 'Not HP HCA — fiber is close but a small extension is needed; network not deployed for budgetary reasons',
    2012: 'Blocked by Unifiber decision (outside of polygon, Greenfields from another operator, financial decision, existing fiber from another operator, …)',
    2013: 'Greenfield unit',
    2014: 'On roll Outside (outside infrastructure existing but not connected to our network, …)',
    2015: 'On roll Inside (vertical cabling done but no fiber introduced into the building, …)',
};
export const NOT_HOMES_PASSED_REASONS = {
    2101: 'Address has been identified as not existing',
    2102: 'Building is under construction or in renovation',
    2103: 'No trenching permit obtained, Sper periode, Synergy delayed, polluted soil',
    2104: 'Outside building obstruction (building blocked by another building, building empty, protected garden/façade, …)',
    2105: 'Inside building obstruction (inside cabling not allowed, plastic cabletray not allowed, no inside technical solution, protected building, …)',
    2106: 'Blocked by syndic or promotor (agreement not yet obtained, agreement rejected, no access granted into the building, …)',
    2107: 'On roll Outside (outside infrastructure existing but not connected to our network, …)',
    2108: 'On roll Inside (vertical cabling done but no fiber introduced into the building, …)',
    2109: 'Unit inside the polygon but not foreseen to be connected (not a residential, business or public building)',
    2110: 'Blocked by Unifiber due to a fiber capacity problem',
    2111: 'Blocked by Unifiber decision (outside of polygon, financial decision, existing fiber from another operator, …)',
    2112: 'Other',
};
export const NEW_DELIVERY_STATUS = {
    1101: { label: 'Home Passed is not yet started', group: 'HP_WIP', quadrant: null, networkType: null },
    1102: { label: 'Home Passed planned, not yet started', group: 'HP_WIP', quadrant: null, networkType: null },
    1103: { label: 'Home Passed WIP (works ongoing in front of the unit)', group: 'HP_WIP', quadrant: null, networkType: null },
    1104: { label: 'Home Passed via Floor-Box, quadrant A', group: 'HP_HCA', quadrant: 'A', networkType: 'FLOOR_BOX' },
    1105: { label: 'Home Passed via Budi-Box, quadrant C', group: 'HP_HCA', quadrant: 'C', networkType: 'BUDI_BOX' },
    1106: { label: 'Home Passed via Facade-Dropbox, quadrant D', group: 'HP_HCA', quadrant: 'D', networkType: 'FACADE_DROPBOX' },
    1107: { label: 'Home Passed via Pole-Dropbox, quadrant P', group: 'HP_HCA', quadrant: 'P', networkType: 'POLE_DROPBOX' },
    1108: { label: 'Home Passed via Multifu, quadrant M', group: 'HP_HCA', quadrant: 'M', networkType: 'MULTIFU' },
    1109: { label: 'Home Passed Plus via Multifu, quadrant M - waiting tube introduced in the house', group: 'HP_HCA', quadrant: 'M', networkType: 'MULTIFU' },
    1110: { label: 'Homes Passed Plus, quadrant P - waiting tube installed between pole and house', group: 'HP_HCA', quadrant: 'P', networkType: 'POLE_DROPBOX' },
    1111: { label: 'Home Passed via outside (facade or underground) plastic-box link to Multifu, quadrant Mx', group: 'HP_HCA', quadrant: 'Mx', networkType: 'FACADE_PLASTIC_BOX' },
    1112: { label: 'Home Passed via inside (basement/technical room) plastic-box link to Multifu, quadrant Mx', group: 'HP_HCA', quadrant: 'Mx', networkType: 'FACADE_PLASTIC_BOX' },
    1113: { label: 'Home Connected via Floor-Box, quadrant A', group: 'HC', quadrant: 'A', networkType: 'FLOOR_BOX' },
    1114: { label: 'Home Connected via Budi-Box, quadrant C', group: 'HC', quadrant: 'C', networkType: 'BUDI_BOX' },
    1115: { label: 'Home Connected via Facade-Dropbox, quadrant D', group: 'HC', quadrant: 'D', networkType: 'FACADE_DROPBOX' },
    1116: { label: 'Home Connected via Pole-Dropbox, quadrant P', group: 'HC', quadrant: 'P', networkType: 'POLE_DROPBOX' },
    1117: { label: 'Home Connected via Multifu, quadrant M', group: 'HC', quadrant: 'M', networkType: 'MULTIFU' },
    1118: { label: 'Home Connected via outside (facade or underground) plastic-box link to Multifu, quadrant Mx', group: 'HC', quadrant: 'Mx', networkType: 'FACADE_PLASTIC_BOX' },
    1119: { label: 'Home Connected via inside (basement/technical room) plastic-box link to Multifu, quadrant Mx', group: 'HC', quadrant: 'Mx', networkType: 'FACADE_PLASTIC_BOX' },
    1120: { label: 'Unit is blocked - consult the Not-Homes-Passed codes', group: 'BLOCKED', quadrant: null, networkType: null },
};
/** Short family label shown next to the code, mirroring how the composite shows its deployment family. */
const NEW_GROUP_LABEL = {
    HP_WIP: 'HP WIP',
    HP_HCA: 'HP HCA',
    HC: 'Homes Connected',
    BLOCKED: 'Blocked',
};
/** The new-scheme entry for a code, or null when it belongs to another scheme. */
export function newDeliveryCode(code) {
    if (code == null)
        return null;
    return NEW_DELIVERY_STATUS[code] ?? null;
}
/** The blocked-family label + exact reason, or null when the code is not a blocked one. */
export function blockedDeliveryReason(code) {
    if (code == null)
        return null;
    if (code in BLOCKED_DELIVERY_REASONS)
        return { family: 'Blocked', reason: BLOCKED_DELIVERY_REASONS[code] };
    if (code in NOT_HOMES_PASSED_REASONS)
        return { family: 'Not-Homes Passed', reason: NOT_HOMES_PASSED_REASONS[code] };
    // The new scheme has exactly one blocked value. Routing it here rather than special-casing it in five
    // places gives it the blocked behaviour everywhere at once: no release, NOT_AVAILABLE, no fiber
    // derivation, and the "delivery blocked" attention rule fires.
    const neu = newDeliveryCode(code);
    if (neu?.group === 'BLOCKED')
        return { family: 'Blocked', reason: neu.label };
    return null;
}
/** True for the 2001-2015 / 2101-2112 blocked codes — the address is NOT connectable as-is. */
export const isBlockedDeliveryCode = (code) => blockedDeliveryReason(code) != null;
/** The zero-padded 4-char form, or null when `code` is absent / not a 4-digit composite. */
function fourDigits(code) {
    if (code == null || !Number.isInteger(code) || code < 0)
        return null;
    const s = String(code).padStart(4, '0');
    return s.length === 4 ? s : null;
}
export function decodeDeliveryStatus(code) {
    if (code == null)
        return null;
    // Blocked codes decode to their EXACT reason (flat tables above), never positionally.
    const blocked = blockedDeliveryReason(code);
    if (blocked)
        return { raw: code, deployment: blocked.family, network: blocked.reason, status: null };
    // New flat scheme BEFORE the positional read - 1108 is one value, not 1/10/8.
    const neu = newDeliveryCode(code);
    if (neu)
        return { raw: code, deployment: NEW_GROUP_LABEL[neu.group], network: neu.label, status: null };
    const s = fourDigits(code);
    if (!s)
        return { raw: code, deployment: null, network: null, status: null };
    return {
        raw: code,
        deployment: DEPLOYMENT[s[0]] ?? null,
        network: NETWORK[s.slice(1, 3)] ?? null,
        status: DELIVERY[s[3]] ?? null,
    };
}
/** One-line display: "9055 — Aerial · Pole-Dropbox, Quadrant P (SDU) · HP HCA". '—' when null. */
export function formatDeliveryStatus(code) {
    const d = decodeDeliveryStatus(code);
    if (!d)
        return '—';
    const parts = [d.deployment, d.network, d.status].filter(Boolean);
    return parts.length ? `${d.raw} — ${parts.join(' · ')}` : String(d.raw);
}
/**
 * Valid 4-digit composite: digit1 a known deployment ({2,7,8,9}), digits2-3 a known network (01..07),
 * digit4 a known delivery status (1..5). Used by the Create-address validation (reject e.g. 9999).
 */
export function isValidDeliveryCode(code) {
    if (isBlockedDeliveryCode(code))
        return true; // exact blocked codes are valid (e.g. 2004 has no positional decode)
    if (newDeliveryCode(code))
        return true; // the new scheme is flat: membership IS validity
    const s = fourDigits(code);
    if (!s)
        return false;
    return s[0] in DEPLOYMENT && s.slice(1, 3) in NETWORK && s[3] in DELIVERY;
}
/** True when digit4 === '5' (HP HCA) — the address is Homes-Passed. Mirrors isV3HomesPassed in the source
 *  DeliveryStatusHelper: '4' is HP-WIP (NOT passed) and '6' is Homes-Connect (handled separately). */
export function canHomeRelease(code) {
    if (isBlockedDeliveryCode(code))
        return false; // 2005/2015/2105 end in '5' but are BLOCKED, not HP HCA
    // New scheme: HP_HCA is the exact equivalent of the composite's digit4 '5'. HC (1113-1119) is already
    // connected, so it is excluded here for the same reason digit4 '6' is.
    const neu = newDeliveryCode(code);
    if (neu)
        return neu.group === 'HP_HCA';
    const s = fourDigits(code);
    if (!s)
        return false;
    return s[3] === '5';
}
/** Address `status` derived from the delivery code on create, mirroring addressStatusFromDeliveryStatus in
 *  the source: digit4 '5' → HOMES_PASSED, '6' → HOMES_CONNECT, otherwise NOT_AVAILABLE. */
export function addressStatusForDeliveryCode(code) {
    if (isBlockedDeliveryCode(code))
        return 'NOT_AVAILABLE'; // 2xx6 blocked codes are not Homes-Connect
    const neu = newDeliveryCode(code);
    if (neu) {
        if (neu.group === 'HC')
            return 'HOMES_CONNECT';
        return neu.group === 'HP_HCA' ? 'HOMES_PASSED' : 'NOT_AVAILABLE'; // HP_WIP is not passed yet
    }
    const s = fourDigits(code);
    if (s && s[3] === '6')
        return 'HOMES_CONNECT';
    return canHomeRelease(code) ? 'HOMES_PASSED' : 'NOT_AVAILABLE';
}
/** Fiber `deployment_type` stand-in from the network group (digits 2-3), mirroring the value the source
 *  stores; null when the code is absent/non-composite. */
export function deploymentTypeFor(code) {
    if (isBlockedDeliveryCode(code))
        return null; // digits 2-3 of a blocked code are not a network type
    const neu = newDeliveryCode(code);
    if (neu)
        return neu.networkType;
    const s = fourDigits(code);
    return s ? (FIBER_NETWORK_TYPE[s.slice(1, 3)] ?? null) : null;
}
/** Fiber `quadrant` letter from digits 2-3, or null when the code is absent/non-composite. */
export function quadrantFor(code) {
    if (isBlockedDeliveryCode(code))
        return null;
    const neu = newDeliveryCode(code);
    if (neu)
        return neu.quadrant;
    const s = fourDigits(code);
    return s ? (QUADRANT[s.slice(1, 3)] ?? null) : null;
}
/**
 * The codes a reviewer may PICK when correcting an address's delivery status (user decisions
 * 2026-08-28). Built from the tables above rather than from a new list, so a code can never be
 * offerable and undecodable at the same time.
 *
 * The new flat family comes first -- it is the norm from 1 September 2026 -- then the composite, which
 * ~396 000 addresses still carry and which therefore stays correctable (7013 -> 7015 is an ordinary
 * fix).
 *
 * BLOCKED codes are deliberately absent: the 2001-2015 / 2101-2112 tables and the new scheme's 1120.
 * Declaring an address non-serviceable is a business decision, not a transcription fix. They remain
 * ACCEPTED when typed by hand -- isValidDeliveryCode knows them -- so nothing is made impossible, only
 * un-suggested. Composite codes starting with '2' are skipped for the same reason (and they would
 * collide with the flat blocked tables).
 */
export function listSelectableDeliveryCodes() {
    const out = [];
    for (const key of Object.keys(NEW_DELIVERY_STATUS)) {
        const code = Number(key);
        if (NEW_DELIVERY_STATUS[code].group === 'BLOCKED')
            continue;
        out.push({ code, label: formatDeliveryStatus(code), family: 'new' });
    }
    for (const deployment of ['7', '8', '9']) {
        for (const network of Object.keys(NETWORK)) {
            for (const delivery of Object.keys(DELIVERY)) {
                const code = Number(deployment + network + delivery);
                out.push({ code, label: formatDeliveryStatus(code), family: 'composite' });
            }
        }
    }
    return out;
}
//# sourceMappingURL=delivery-status.js.map